<template>
    <df-admin-form :editing="isEditing" :loading="loading" @submit="submit" @cancel="$router.push({ name: 'admin-solutions' })">

        <div class="email-compose-fields">
            <text-field horizontal label="Bezeichnung / Titel" v-model="data.title" :validation="$v.data.title" @focus="checkDuplicates(true)" @blur="checkDuplicates(false)">
                <be-alert
                    class="mt-1 mb-0"
                    color="warning"
                    icon="alert-triangle"
                    contrast
                    simple
                    icon-colored
                    v-if="titleFocussed && similarSolutions.length"
                >
                    <strong>Potentielle Duplikate gefunden</strong>
                    <ul>
                        <li v-for="solution in similarSolutions" :key="solution.id">
                            <router-link :to="{ name: 'edit-solution', params: { id: solution.id} }" :title="`Solutions «${solution.title}» bearbeiten`">{{solution.title}}</router-link>
                        </li>
                    </ul>
                </be-alert>
            </text-field>
            <text-field horizontal label="Website" v-model="data.website" :validation="$v.data.website"/>
            <radio-checkbox
                horizontal
                inline
                name="type"
                label="Typ"
                :options="typeOptions"
                v-model="data.type"
                :validation="$v.data.type"
            >
                <p slot="description">Service/Tool = fertige Lösungen bspw. Channel-Managent oder PMS.<br>Nano-Degrees bezieht sich auf den Wissenstransfer / Ausbildung bspw. Tutorial zu Revenue-Management</p>
            </radio-checkbox>
            <text-area horizontal label="Nutzen" v-model="data.benefit" :validation="$v.data.benefit"/>
        </div>
        <div class="email-compose-fields">
            <text-field horizontal label="YouTube Video ID" v-model="data.youtubeId" :validation="$v.data.youtubeId"/>
            <select-list horizontal multiple :searchable="false" label="Kategorien" :options="categoryOptions" v-model="data.frameworkCategories" :validation="$v.data.frameworkCategories"/>
            <tag-list horizontal multiple label="Tags" :options="tagOptions" v-model="data.tags" :validation="$v.data.tags"/>
        </div>
        <div class="email-compose-fields">
            <text-field horizontal label="Kosten einmalig" description="ab CHF (für Setup und Einrichtung), unverbindliche Richtwerte" v-model="data.costsOneTime" :validation="$v.data.costsOneTime"/>
            <text-field horizontal label="Kosten wiederkehrend pro Jahr" description="ab CHF (bspw. für Lizenz / Wartung), unverbindliche Richtwerte" v-model="data.costsRecurring" :validation="$v.data.costsRecurring"/>
            <text-field horizontal label="Bemerkungen zu Kosten" description="bspw. Abhängig vom Umsatz / pro Arbeitsplatz usw." v-model="data.costsNotes" :validation="$v.data.costsNotes"/>
            <select-menu horizontal label="Zeitaufwand" :options="timeNeededOptions" v-model="data.timeNeeded" :validation="$v.data.timeNeeded"/>
        </div>

    </df-admin-form>
</template>

<script>
    import arrayUniq from 'array-uniq';
    import { required, url, decimal, maxLength } from 'vuelidate/lib/validators';
    import { regex, withParams } from 'vuelidate/lib/validators/common';
    import adminForm from '../../../../mixins/adminForm';
    import FrameworkCategories from '../../../../data/FrameworkCategories';
    import Solution from '../../../../models/Solution';
    import Solutions from '../../../../data/Solutions';

    export default {
        mixins: [adminForm],

        data: () => ({
            model: Solution,
            titleFocussed: false,
        }),

        props: {
            allRows: Array,
        },

        validations: {
            data: {
                type: { required },
                title: {
                    required,
                    maxlength: maxLength(255),
                    duplicate(value) {
                        const vm = this;

                        return (withParams(
                            { message: 'Eine Solution mit diesem Namen ist bereits vorhanden.' },
                            () => value === '' || vm.allRows.find(s => s.title === value && s.id !== vm.data.id) === undefined,
                        ))();
                    },
                },
                website: { required, url, maxlength: maxLength(255) },
                benefit: { required },
                youtubeId: { youtube: withParams({ message: 'Geben Sie eine gültige YouTube ID ein.' }, regex('youtube', /^[\w-]{11}$/)) },
                tags: {},
                frameworkCategories: {},
                costsOneTime: { required, decimal, maxlength: maxLength(255) },
                costsRecurring: { required, decimal, maxlength: maxLength(255) },
                costsNotes: { maxlength: maxLength(255) },
                timeNeeded: { required },
            },
        },

        computed: {
            typeOptions: () => Object.keys(Solution.TypeMap).map(t => ({ value: t, label: Solution.TypeMap[t] })),
            timeNeededOptions: () => Object.keys(Solution.TimeNeededMap).map(t => ({ value: t, label: Solution.TimeNeededMap[t] })),
            titleOptions: vm => vm.allRows.map(s => ({ value: s.title, label: s.title })),

            similarSolutions() {
                if (!this.data || !this.data.title) {
                    return [];
                }

                const words = this.data.title.split(' ').map(w => w.toLowerCase()).filter(w => w.length > 2);

                if (!words.length) {
                    return [];
                }

                return this.allRows.filter(
                    s => words.some(w => s.title.toLowerCase().includes(w)) && s.id !== this.data.id,
                );
            },

            tagOptions() {
                return arrayUniq(
                    this.allRows.reduce((v, s) => v.concat(s.tags), []),
                );
            },
        },

        asyncComputed: {
            categoryOptions: async () => (await FrameworkCategories.all()).map(c => ({
                value: c.resourceId,
                label: c.title,
            })),
        },

        methods: {
            checkDuplicates(v) {
                if (v) {
                    this.titleFocussed = true;
                } else {
                    setTimeout(
                        () => {
                            this.titleFocussed = false;
                        },
                        300,
                    );
                }
            },

            async submit() {
                this.data.timeNeeded = Number(this.data.timeNeeded);

                if (await this.saveForm()) {
                    Solutions.clear();
                    this.$router.push({ name: 'admin-solutions' });
                }
            },
        },
    };
</script>
